/**
 * `persistentHighlightData.ts` — web port of Unity
 * `Models/Data/PersistentHighlightData.cs`, together with the visual-state math
 * from `Models/PersistentEntityHighlightModelBase.cs` and the desktop tuning
 * constants from `CampfireScenes/.../PersistentEntityHighlightDesktopModel.cs`.
 *
 * The payload uses Unity's JsonUtility string shape. Web reads legacy
 * `PersistentHighlightJson` snapshot attributes and compatible peer payloads,
 * but treats highlights as runtime state and never writes that DynamoDB
 * attribute:
 *
 *   {"enabled":true,"semantic":"hazard","colorHex":"","pulseMode":"continuous",
 *    "pulseCount":-1,"thicknessMode":"thick"}
 *
 * Contract facts kept verbatim (`PersistentHighlightData.cs`):
 *   - `Serialize` returns null (attribute omitted / cleared) when the highlight
 *     is disabled — "no highlight" is the ABSENCE of the payload.
 *   - `Deserialize` returns null for blank/invalid/disabled JSON and normalizes
 *     every field through the alias tables below.
 *   - `pulseCount` is persisted as the RAW int: `-1` means infinite, and any
 *     value `<= 0` normalizes to `-1`.
 *   - `colorHex` is persisted normalized to `#RRGGBB` (uppercase, alpha
 *     dropped), and JsonUtility writes a null string as `""`.
 *
 * Colors are DISPLAY values, not light-linear ones: Unity stores its `Color32`
 * semantics in gamma space and shows them as-is, so `resolveHighlightColor`
 * returns raw 0..1 components meant for `Color.setRGB` with no conversion.
 * See `runtime/engine/COLOR_PIPELINE.md` — the outline colors are Population B.
 *
 * ⚠ WEB DEVIATIONS — the "Part Highlight — Halo (3a)" design handoff
 * (2026-09-28) replaced the Unity desktop LOOK, while the persisted contract
 * above stays Unity's. It SUPERSEDES the inner-glow handoff of 2026-09-15: the
 * glow moved outside the silhouette and the line stopped animating, on the
 * team's feedback that the old treatment "bled across the part and onto its
 * neighbours" and read as a radioactive warning.
 *   - The semantic palette is the design's (`highlight/default` #FFD233,
 *     `highlight/success` #2E9E54, `highlight/alert` #F84536), not Unity's
 *     `Color32` values. Its optional `highlight/amber` #E8A33D and
 *     `highlight/info` #0F99D4 are reached through `colorHex`: a fourth
 *     semantic would be a value a desktop peer folds back to attention.
 *   - STEADY IS THE DEFAULT, reversing Unity's continuous default — "pulsing
 *     is an opt-in, for moments that need to pull the eye". Only what the web
 *     CREATES changed (`DEFAULT_PERSISTENT_HIGHLIGHT`); a payload that OMITS
 *     `pulseMode` still parses as continuous, because that is what a desktop
 *     peer means by omitting it.
 *   - Only the HALO animates: 13 → 30 → 13 % on a 2800 ms cycle, eased
 *     `cubic-bezier(.4,0,.4,1)` per half, resting at 25 % when steady — about a
 *     THIRD of the handoff's own 38 / 85 / 72 %, by user direction after two
 *     rounds in the app (see `K_HALO_STRENGTH`), with the reach pulled from
 *     13 px to 8. The line is 100 % always. So a `pulseCount` highlight runs
 *     `pulseCount × pulseMs` here, and a desktop peer in the same session may
 *     clear it sooner (whichever client expires first persists the clear).
 *   - The line and halo STEP DOWN on a part that is small on screen (three
 *     levels, `highlightLevelForSize`), and below the ladder's bottom step they
 *     are additionally capped against the part's own size
 *     (`highlightLineWidthForSize` / `highlightHaloReachForSize`), so a
 *     highlighted screw is outlined rather than swallowed. `thicknessMode` is
 *     the ceiling; naming an exact `outlineWidth` / `haloReach` opts out. This
 *     is a deliberate departure from the handoff's "sizes stay constant in
 *     screen space".
 *   - Three OPTIONAL web-only fields ride the same JSON — `pulseMs`,
 *     `outlineWidth` (CSS px), `haloReach` (CSS px) — written only when set, so
 *     an unset highlight serializes byte-identically to Unity's. JsonUtility
 *     ignores them on read; a desktop REWRITE drops them and the web falls
 *     back to the `thicknessMode` preset, which is the intended degradation.
 *     `haloReach` replaces the inner-glow era's `glowReach`, which is still
 *     READ (clamped into the new range) so a highlight written while that
 *     design shipped keeps a custom size instead of silently losing it.
 */

export const PersistentHighlightSemantics = {
  Attention: "attention",
  Correct: "correct",
  Hazard: "hazard",
} as const;

export const PersistentHighlightPulseModes = {
  SingleSoft: "singleSoft",
  Continuous: "continuous",
  None: "none",
} as const;

export const PersistentHighlightThicknessModes = {
  Thin: "thin",
  Medium: "medium",
  Thick: "thick",
} as const;

export type PersistentHighlightSemantic =
  (typeof PersistentHighlightSemantics)[keyof typeof PersistentHighlightSemantics];
export type PersistentHighlightPulseMode =
  (typeof PersistentHighlightPulseModes)[keyof typeof PersistentHighlightPulseModes];
export type PersistentHighlightThicknessMode =
  (typeof PersistentHighlightThicknessModes)[keyof typeof PersistentHighlightThicknessModes];

export interface PersistentHighlightData {
  readonly enabled: boolean;
  readonly semantic: PersistentHighlightSemantic;
  /** Normalized `#RRGGBB`, or `null` when the semantic color is used. */
  readonly colorHex: string | null;
  readonly pulseMode: PersistentHighlightPulseMode;
  /** Positive cycle count, or `null` for infinite (persisted as `-1`). */
  readonly pulseCount: number | null;
  readonly thicknessMode: PersistentHighlightThicknessMode;
  /** Web-only: one pulse cycle in ms, or `null` for the design default. */
  readonly pulseMs: number | null;
  /** Web-only: line width in CSS px, or `null` for the thickness preset. */
  readonly outlineWidth: number | null;
  /** Web-only: outward halo reach in CSS px, or `null` for the thickness preset. */
  readonly haloReach: number | null;
}

export type Rgb = readonly [number, number, number];

// Design handoff "Part Highlight — Halo (3a)", 2026-09-28. Its README states
// these numbers are the spec; the prototype page's own 2200 ms is not.
export const HIGHLIGHT_DEFAULT_PULSE_MS = 2800;
export const HIGHLIGHT_PULSE_MS_RANGE = { min: 1200, max: 4000 } as const;
/**
 * The line width, centred on the silhouette (half in, half out). ⚠ 4, not the
 * handoff's 5, by user direction 2026-09-29 after comparing the weights on a
 * real model: the ceiling is what the top level draws and what the two lighter
 * levels are scaled from, so lowering it moves all three together (2.4 / 3.2 /
 * 4) rather than only helping small parts.
 *
 * The RANGE stays the handoff's: it is what a caller may NAME, and an explicit
 * width is answered exactly. The automatic path can go finer than its floor —
 * it is bounded by `HIGHLIGHT_MIN_LINE_WIDTH_PX` instead.
 */
export const HIGHLIGHT_DEFAULT_OUTLINE_WIDTH = 4;
export const HIGHLIGHT_OUTLINE_WIDTH_RANGE = { min: 3, max: 16 } as const;
/**
 * The WIDE band's outward reach from the edge. ⚠ 8, not the handoff's 13, by
 * the same user direction as the strength above: pulling the reach in drops the
 * outer bloom while keeping the bright rim against the line, where more opacity
 * cutting would have faded the rim too. The handoff's 13 remains what its
 * published alpha table is specified at, and the full 3–18 range is still
 * reachable through `haloReach`.
 */
export const HIGHLIGHT_DEFAULT_HALO_REACH = 8;
export const HIGHLIGHT_HALO_REACH_RANGE = { min: 3, max: 18 } as const;
/** Pulsing ↔ steady, and a highlight's fade-in: "280ms — no jump". */
export const HIGHLIGHT_TRANSITION_SECONDS = 0.28;

/**
 * ⚠ SIZE-ADAPTIVE WEIGHT (user direction 2026-09-29) — a DEPARTURE from the
 * handoff's "sizes stay constant in screen space: they do not scale with camera
 * distance or model scale".
 *
 * A line and halo in fixed screen pixels swallow a part that is small on
 * screen: a row of highlighted screws on the demo robot read as yellow
 * lozenges, because 5 px of line and 8 px of halo are most of a 14 px screw.
 * So the weight steps DOWN in three levels — the user's "low / medium / high
 * thickness levels depending on size of the model" — rather than scaling
 * continuously, which would make the line a different weight in every frame of
 * an orbit.
 *
 * ⚠ The size that picks the level is the part's THINNEST dimension, in CSS px
 * at its distance (`thinnestExtentPx` in `PartHighlightPass`), NOT its screen
 * footprint. A flat top cover has a large footprint and still reads as a plate,
 * so a footprint measure gave it the heaviest line (user report 2026-09-29);
 * its thinness is what makes it flat. The same measure covers a slender part
 * lying diagonally, which an axis-aligned screen box over-reports.
 *
 * `thicknessMode` still decides the weight, but as a CEILING: `thick` (the
 * default) means "up to 5 px, less on a small part", and `thin` pins the top
 * level low. An explicit `outlineWidth` / `haloReach` opts out entirely — a
 * caller who named a pixel size gets that size.
 */
export const HIGHLIGHT_LEVEL_FACTORS = [0.6, 0.8, 1] as const;
/** The part's thinnest dimension (CSS px) at which each level takes over. */
export const HIGHLIGHT_LEVEL_STEPS = [28, 72] as const;
/**
 * A level change needs the size to pass the step by this fraction, so a part
 * hovering on a threshold during an orbit does not flicker between weights.
 */
export const HIGHLIGHT_LEVEL_HYSTERESIS = 0.12;
/** However small the part, the line stays visible. */
export const HIGHLIGHT_MIN_LINE_WIDTH_PX = 1.5;
/** …and the halo stays a halo rather than collapsing into the line. */
export const HIGHLIGHT_MIN_HALO_REACH_PX = 1.4;

/**
 * ⚠ THE SMALL-PART CLAMP (user direction 2026-09-30) — the ladder above sets a
 * weight, and these cap it against the part's OWN size.
 *
 * The ladder's bottom level is an unbounded bucket: everything thinner than its
 * first step (28 CSS px) draws the same 2.4 px line and 4.8 px halo. A row of
 * pan-head screws reported on the battery-removal project measures ~6.6 px
 * across the shank — four times below that step — so it was handed the weight
 * of a 27 px bracket. At that size the line eats 36 % of the shank and the halo
 * adds 1.7x the screw's width, which is the "glowing lozenge" the user saw.
 *
 * So the line may not exceed this fraction of the part's thinnest dimension,
 * and the halo may not reach past its own. The clamp can only ever REDUCE the
 * ladder's weight, and the fractions put it out of reach above ~11 px (line)
 * and ~15 px (halo) of thinness: every part already signed off on — covers,
 * brackets, batteries — renders byte-identically, and only the screw regime
 * moves.
 *
 * Why a clamp rather than a fourth level: another rung leaves the same defect
 * one step lower, since the new bottom bucket is unbounded below too, and the
 * line reaches `HIGHLIGHT_MIN_LINE_WIDTH_PX` almost immediately so further
 * rungs stop changing it. The flicker risk that made the ladder discrete does
 * not apply to a continuous term here either: thinness is a world size over
 * camera distance, so it holds still through an orbit and moves only on a
 * dolly.
 */
export const HIGHLIGHT_SIZE_LINE_FRACTION = 0.22;
export const HIGHLIGHT_SIZE_HALO_FRACTION = 0.32;

/**
 * Which weight level a part takes, from its thinnest dimension in CSS px and
 * the level it held last frame (`previousLevel`, or `null` when it is new).
 * Levels are indices into `HIGHLIGHT_LEVEL_FACTORS`: 0 low, 1 medium, 2 high.
 */
export function highlightLevelForSize(
  projectedSizePx: number,
  previousLevel: number | null = null,
): number {
  const steps = HIGHLIGHT_LEVEL_STEPS;
  let level = 0;
  while (level < steps.length && projectedSizePx >= steps[level]) level++;
  if (previousLevel === null || previousLevel === level) return level;
  // Moving to a new level only counts once the size is clear of the step it
  // would have to cross back over.
  const step = level > previousLevel ? steps[previousLevel] : steps[level];
  const margin = step * HIGHLIGHT_LEVEL_HYSTERESIS;
  const cleared = level > previousLevel
    ? projectedSizePx >= step + margin
    : projectedSizePx <= step - margin;
  return cleared ? level : previousLevel;
}

/**
 * Caps a ladder weight against the part's own thinnest dimension. Returns the
 * ladder weight untouched when the part is big enough, or when its size is
 * unknown (`Infinity` — no trustworthy bounds, or the part straddles the eye).
 */
function clampWeightToSize(
  ladderPx: number,
  sizeCss: number,
  fraction: number,
  floorPx: number,
): number {
  if (!Number.isFinite(sizeCss)) return Math.max(floorPx, ladderPx);
  return Math.max(floorPx, Math.min(ladderPx, sizeCss * fraction));
}

/** The line width a part actually draws: its level's, capped by its size. */
export function highlightLineWidthForSize(ladderPx: number, sizeCss: number): number {
  return clampWeightToSize(
    ladderPx,
    sizeCss,
    HIGHLIGHT_SIZE_LINE_FRACTION,
    HIGHLIGHT_MIN_LINE_WIDTH_PX,
  );
}

/** The halo reach a part actually draws: its level's, capped by its size. */
export function highlightHaloReachForSize(ladderPx: number, sizeCss: number): number {
  return clampWeightToSize(
    ladderPx,
    sizeCss,
    HIGHLIGHT_SIZE_HALO_FRACTION,
    HIGHLIGHT_MIN_HALO_REACH_PX,
  );
}

/** "100%, always. Never pulses, never fades" — the line carries no motion. */
export const HIGHLIGHT_LINE_OPACITY = 1;

/** The handoff's published halo opacities, before the strength cut below. */
const K_HANDOFF_HALO_TROUGH = 0.38;
const K_HANDOFF_HALO_PEAK = 0.85;
const K_HANDOFF_HALO_STEADY = 0.72;
/**
 * ⚠ THE HALO SHIPS AT A FRACTION OF THE HANDOFF'S STRENGTH — 25 % steady where
 * the design says 72 % — by user direction (2026-09-29, over two rounds in the
 * app: "the glow is way too much … reduced by 50% at least", then "the glow is
 * a bit much for smaller parts"). A fixed screen-space halo covers
 * proportionally more of a small part, which is what made the published value
 * read as a yellow band rather than a halo.
 *
 * Expressed as ONE scale over the design's own numbers rather than three tuned
 * constants, so the pulse keeps the proportion the design gave it (trough and
 * peak stay 0.53× and 1.18× the steady value) and the next tuning pass is one
 * line. The two-band falloff, its feathers and the line are untouched: this
 * scales the handoff's curve, it does not reshape it.
 */
const K_HALO_STRENGTH = 0.25 / K_HANDOFF_HALO_STEADY;
const K_HALO_OPACITY_TROUGH = K_HANDOFF_HALO_TROUGH * K_HALO_STRENGTH;
const K_HALO_OPACITY_PEAK = K_HANDOFF_HALO_PEAK * K_HALO_STRENGTH;
const K_HALO_OPACITY_STEADY = K_HANDOFF_HALO_STEADY * K_HALO_STRENGTH;

/**
 * What the web CREATES for a new highlight: Unity's default instance except for
 * `pulseMode`, which the Halo handoff rests at steady (file header). Parsing a
 * payload that omits the field is unaffected — that still reads as continuous.
 */
export const DEFAULT_PERSISTENT_HIGHLIGHT: PersistentHighlightData = {
  enabled: true,
  semantic: PersistentHighlightSemantics.Attention,
  colorHex: null,
  pulseMode: PersistentHighlightPulseModes.None,
  pulseCount: null,
  thicknessMode: PersistentHighlightThicknessModes.Thick,
  pulseMs: null,
  outlineWidth: null,
  haloReach: null,
};

// -- Normalization -----------------------------------------------------------

/** Unity's alias folding: trim, lowercase, strip `_`, `-` and spaces. */
function fold(value: string): string {
  return value.trim().toLowerCase().replace(/[-_ ]/g, "");
}

export function normalizeHighlightSemantic(value: unknown): PersistentHighlightSemantic {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightSemantics.Attention;
  }
  switch (fold(value)) {
    case "attention":
    case "focus":
    case "focused":
    case "current":
    case "currentfocus":
    case "yellow":
      return PersistentHighlightSemantics.Attention;
    case "correct":
    case "completed":
    case "complete":
    case "success":
    case "green":
      return PersistentHighlightSemantics.Correct;
    case "hazard":
    case "error":
    case "danger":
    case "warning":
    case "red":
      return PersistentHighlightSemantics.Hazard;
    default:
      return PersistentHighlightSemantics.Attention;
  }
}

export function normalizeHighlightPulseMode(value: unknown): PersistentHighlightPulseMode {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightPulseModes.Continuous;
  }
  switch (fold(value)) {
    case "single":
    case "singlesoft":
    case "soft":
    case "once":
      return PersistentHighlightPulseModes.SingleSoft;
    case "continuous":
    case "loop":
    case "looping":
    case "infinite":
    case "infinitely":
    case "forever":
    case "pulse":
    case "pulsing":
      return PersistentHighlightPulseModes.Continuous;
    case "none":
    case "off":
    case "steady":
    case "static":
      return PersistentHighlightPulseModes.None;
    default:
      return PersistentHighlightPulseModes.Continuous;
  }
}

export function normalizeHighlightThicknessMode(
  value: unknown,
): PersistentHighlightThicknessMode {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightThicknessModes.Thick;
  }
  switch (fold(value)) {
    case "medium":
    case "mid":
    case "moderate":
      return PersistentHighlightThicknessModes.Medium;
    case "thick":
    case "heavy":
    case "heavier":
    case "bold":
    case "strong":
    case "default":
    case "normal":
    case "regular":
    case "standard":
    case "max":
    case "maximum":
      return PersistentHighlightThicknessModes.Thick;
    case "thin":
    case "thinner":
    case "subtle":
    case "light":
    case "slim":
    case "narrow":
      return PersistentHighlightThicknessModes.Thin;
    default:
      return PersistentHighlightThicknessModes.Thick;
  }
}

/** Unity `NormalizePulseCount`: anything `<= 0` (or absent) means infinite. */
export function normalizeHighlightPulseCount(value: unknown): number | null {
  const count = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(count)) return null;
  const whole = Math.trunc(count);
  return whole > 0 ? whole : null;
}

export function normalizeHighlightColorHex(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = tryParseHtmlColor(value) ?? tryParseHtmlColor(`#${value.trim()}`);
  return parsed ? toHtmlStringRgb(parsed) : null;
}

/**
 * A web-only numeric knob: absent, non-numeric or `<= 0` means "not set"
 * (`null`, so the default applies); anything else is clamped into the range
 * the design allows rather than refused.
 */
function normalizeOptionalRange(
  value: unknown,
  range: { readonly min: number; readonly max: number },
): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.min(range.max, Math.max(range.min, numeric));
}

export function normalizeHighlightPulseMs(value: unknown): number | null {
  const ms = normalizeOptionalRange(value, HIGHLIGHT_PULSE_MS_RANGE);
  return ms === null ? null : Math.round(ms);
}

export function normalizeHighlightOutlineWidth(value: unknown): number | null {
  return normalizeOptionalRange(value, HIGHLIGHT_OUTLINE_WIDTH_RANGE);
}

export function normalizeHighlightHaloReach(value: unknown): number | null {
  return normalizeOptionalRange(value, HIGHLIGHT_HALO_REACH_RANGE);
}

/** Unity `NormalizeInPlace` over a partial/untrusted shape (+ the web fields). */
export function normalizeHighlight(
  value: Partial<PersistentHighlightData>,
): PersistentHighlightData {
  return {
    enabled: value.enabled !== false,
    semantic: normalizeHighlightSemantic(value.semantic),
    colorHex: normalizeHighlightColorHex(value.colorHex),
    pulseMode: normalizeHighlightPulseMode(value.pulseMode),
    pulseCount: normalizeHighlightPulseCount(value.pulseCount),
    thicknessMode: normalizeHighlightThicknessMode(value.thicknessMode),
    pulseMs: normalizeHighlightPulseMs(value.pulseMs),
    outlineWidth: normalizeHighlightOutlineWidth(value.outlineWidth),
    // `glowReach` is the inner-glow era's spelling of the same knob (header).
    haloReach: normalizeHighlightHaloReach(
      value.haloReach ?? (value as { glowReach?: unknown }).glowReach,
    ),
  };
}

// -- Serialization -----------------------------------------------------------

/** Unity `PersistentHighlightData.Deserialize` — null for blank/invalid/disabled. */
export function parsePersistentHighlightJson(
  json: string | null | undefined,
): PersistentHighlightData | null {
  if (!json || !json.trim()) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  // Unity reads `enabled` off a `[SerializeField] bool` that defaults to true,
  // so a payload that omits it is enabled — but an explicit `false` is not.
  if (record.enabled === false) return null;
  return normalizeHighlight(record as Partial<PersistentHighlightData>);
}

/**
 * Unity `PersistentHighlightData.Serialize` — null when disabled (the caller
 * clears the attribute). Field order and the `""` / `-1` encodings match
 * JsonUtility's output so a Unity peer reads web-written rows unchanged.
 */
export function serializePersistentHighlight(
  highlight: PersistentHighlightData | null,
): string | null {
  if (!highlight || !highlight.enabled) return null;
  const normalized = normalizeHighlight(highlight);
  const payload: Record<string, string | number | boolean> = {
    enabled: true,
    semantic: normalized.semantic,
    colorHex: normalized.colorHex ?? "",
    pulseMode: normalized.pulseMode,
    pulseCount: normalized.pulseCount ?? -1,
    thicknessMode: normalized.thicknessMode,
  };
  // Web-only knobs go LAST and only when set, so a highlight that uses none of
  // them is byte-identical to what JsonUtility writes (see the file header).
  if (normalized.pulseMs !== null) payload.pulseMs = normalized.pulseMs;
  if (normalized.outlineWidth !== null) payload.outlineWidth = normalized.outlineWidth;
  if (normalized.haloReach !== null) payload.haloReach = normalized.haloReach;
  return JSON.stringify(payload);
}

/** Unity `ProjectModel_Entities.PersistentHighlightsEqual` (+ the web fields). */
export function persistentHighlightsEqual(
  left: PersistentHighlightData | null,
  right: PersistentHighlightData | null,
): boolean {
  if (!left || !right) return !left && !right;
  return left.enabled === right.enabled
    && left.semantic === right.semantic
    && left.colorHex === right.colorHex
    && left.pulseMode === right.pulseMode
    && left.pulseCount === right.pulseCount
    && left.thicknessMode === right.thicknessMode
    && left.pulseMs === right.pulseMs
    && left.outlineWidth === right.outlineWidth
    && left.haloReach === right.haloReach;
}

// -- Derived properties ------------------------------------------------------

export function usesCustomHighlightColor(highlight: PersistentHighlightData): boolean {
  return !!highlight.colorHex;
}

export function shouldPlayHighlightEntryPulse(highlight: PersistentHighlightData): boolean {
  return highlight.pulseMode === PersistentHighlightPulseModes.SingleSoft;
}

export function shouldPulseHighlightContinuously(highlight: PersistentHighlightData): boolean {
  return highlight.pulseMode === PersistentHighlightPulseModes.Continuous;
}

/**
 * Unity `ResolveColor` — custom hex wins, else the semantic colour. The
 * semantic palette is the design's tokens, not Unity's `Color32` (header).
 */
export function resolveHighlightColor(highlight: PersistentHighlightData): Rgb {
  if (highlight.colorHex) {
    const parsed = tryParseHtmlColor(highlight.colorHex);
    if (parsed) return parsed;
  }
  switch (highlight.semantic) {
    case PersistentHighlightSemantics.Correct:
      return hexTripletToRgb("2e9e54"); // highlight/success
    case PersistentHighlightSemantics.Hazard:
      return hexTripletToRgb("f84536"); // highlight/alert (Campfire ember)
    default:
      return hexTripletToRgb("ffd233"); // highlight/default
  }
}

/** What a highlight looks like, with every web knob resolved to a number. */
export interface HighlightStyle {
  readonly color: Rgb;
  readonly pulseSeconds: number;
  /** Line width, CSS px, centred on the silhouette (constant in screen space). */
  readonly outlineWidthPx: number;
  /** The wide halo band's outward reach from the silhouette, CSS px. */
  readonly haloReachPx: number;
}

/**
 * `thicknessMode` keeps meaning something: `thick` (the default) is the shipped
 * 4 px line / 8 px halo, and the lighter modes step down from there. Each is a
 * CEILING the size levels scale, so `thin` on a small part is finer still.
 * An explicit `outlineWidth` / `haloReach` wins over the preset.
 */
export function resolveHighlightStyle(highlight: PersistentHighlightData): HighlightStyle {
  let presetOutline = HIGHLIGHT_DEFAULT_OUTLINE_WIDTH;
  let presetHalo = HIGHLIGHT_DEFAULT_HALO_REACH;
  if (highlight.thicknessMode === PersistentHighlightThicknessModes.Medium) {
    presetOutline = 3;
    presetHalo = 6;
  } else if (highlight.thicknessMode === PersistentHighlightThicknessModes.Thin) {
    presetOutline = 2;
    presetHalo = 4;
  }
  return {
    color: resolveHighlightColor(highlight),
    pulseSeconds: (highlight.pulseMs ?? HIGHLIGHT_DEFAULT_PULSE_MS) / 1000,
    outlineWidthPx: highlight.outlineWidth ?? presetOutline,
    haloReachPx: highlight.haloReach ?? presetHalo,
  };
}

// -- AI scene-context labels -------------------------------------------------

/** Unity `GetContextColorLabel`. */
export function highlightContextColorLabel(highlight: PersistentHighlightData): string {
  if (usesCustomHighlightColor(highlight)) {
    return toHtmlStringRgb(resolveHighlightColor(highlight));
  }
  switch (highlight.semantic) {
    case PersistentHighlightSemantics.Correct:
      return "green";
    case PersistentHighlightSemantics.Hazard:
      return "red";
    default:
      return "yellow";
  }
}

/** Unity `GetContextPulseLabel`. */
export function highlightContextPulseLabel(highlight: PersistentHighlightData): string {
  if (highlight.pulseMode === PersistentHighlightPulseModes.None) return "static";
  if (highlight.pulseMode === PersistentHighlightPulseModes.Continuous) {
    if (highlight.pulseCount === null) return "pulsing";
    if (highlight.pulseCount === 1) return "pulse once";
    if (highlight.pulseCount === 2) return "pulse twice";
    return `pulse ${highlight.pulseCount} times`;
  }
  return "pulse once";
}

/** Unity `GetContextThicknessLabel`. */
export function highlightContextThicknessLabel(highlight: PersistentHighlightData): string {
  return highlight.thicknessMode;
}

// -- Visual state ------------------------------------------------------------

/** `cubic-bezier(.4, 0, .4, 1)` — the design's symmetric ease, "never springy". */
export const easeHighlight = cubicBezierTiming(0.4, 0, 0.4, 1);

/**
 * `cubic-bezier(.2, 0, 0, 1)` — a STATE change (pulsing ↔ steady, and a new
 * highlight's fade-in) gets its own curve in the design, not the pulse's:
 * "ease the halo opacity to its target over 280ms … No jumps."
 */
export const easeHighlightTransition = cubicBezierTiming(0.2, 0, 0, 1);

/**
 * Pulse level 0..1 at a phase measured in CYCLES (any real number). CSS applies
 * a keyframe's timing function PER SEGMENT, so the prototype's
 * `0%,100% {.2} 50% {.72}` eases up across the first half and back down across
 * the second — not one curve over the whole cycle.
 */
export function highlightPulseLevel(phaseCycles: number): number {
  const phase = phaseCycles - Math.floor(phaseCycles);
  return phase < 0.5 ? easeHighlight(phase * 2) : 1 - easeHighlight((phase - 0.5) * 2);
}

/**
 * The halo's opacity at pulse `level` (0 trough → 1 peak: 38 → 85 %),
 * crossfaded toward steady (72 %) by `steadyWeight` (0 pulsing → 1 steady).
 * A plain number rather than a pair object, so a caller animating every frame
 * allocates nothing. The line has no counterpart — it holds
 * `HIGHLIGHT_LINE_OPACITY` whatever the motion is doing.
 */
export function highlightHaloOpacity(level: number, steadyWeight: number): number {
  const pulse = K_HALO_OPACITY_TROUGH
    + (K_HALO_OPACITY_PEAK - K_HALO_OPACITY_TROUGH) * clamp01(level);
  return pulse + (K_HALO_OPACITY_STEADY - pulse) * clamp01(steadyWeight);
}

/** `TextTile.k_minVisiblePersistentHighlightThickness`: below it a fading card frame is off. */
const K_CARD_FRAME_MIN_VISIBLE_THICKNESS = 0.04;
/** `TextTile.ApplyHighlightImageColor`'s alpha floor for a fading card frame. */
const K_CARD_FRAME_MIN_PULSE_OPACITY = 0.35;
/** `PersistentEntityHighlightModelBase.GetPeakThickness`, the desktop override (0.62). */
const K_CARD_FRAME_BASE_PEAK_THICKNESS = 0.62;

/** `PersistentHighlightData.GetPeakThicknessBias` — how far `thicknessMode` lifts the peak toward 1. */
function peakThicknessBias(mode: PersistentHighlightThicknessMode): number {
  switch (mode) {
    case PersistentHighlightThicknessModes.Thick:
      return 1;
    case PersistentHighlightThicknessModes.Medium:
      return 0.78;
    default:
      return 0.55;
  }
}

/**
 * A highlighted CARD's frame opacity — port of Unity `TextTile.ApplyHighlightImageColor`
 * fed by `PersistentEntityHighlightModelBase.ComputeVisualState`. A card has no
 * silhouette, so Unity tints the card's own `Highlighted` frame instead of
 * outlining it, and a pulse moves that frame's ALPHA: the pulse swings a
 * "thickness" from 0 to the mode's peak (thick 1, medium 0.92, thin 0.83), the
 * frame takes that as its alpha floored at 0.35, and it switches OFF while the
 * thickness is under 0.04 — so a pulsing card blinks at the trough, as on
 * desktop. A steady highlight is the full colour.
 *
 * `level` is the pulse (0 trough → 1 peak) on the web's shared clock and
 * `steadyWeight` the eased pulsing → steady crossfade, both from
 * `PersistentHighlightRuntime` — so a card pulses in phase with the parts and
 * eases into "steady" over the same 280 ms. Pass `level` 1 when nothing is
 * pulsing, so easing into steady starts from the peak rather than dipping
 * through the blink.
 */
export function cardFrameHighlightOpacity(
  level: number,
  steadyWeight: number,
  thicknessMode: PersistentHighlightThicknessMode,
): number {
  const peak = K_CARD_FRAME_BASE_PEAK_THICKNESS
    + (1 - K_CARD_FRAME_BASE_PEAK_THICKNESS) * peakThicknessBias(thicknessMode);
  const thickness = clamp01(level) * peak;
  const pulse = thickness > K_CARD_FRAME_MIN_VISIBLE_THICKNESS
    ? Math.max(thickness, K_CARD_FRAME_MIN_PULSE_OPACITY)
    : 0;
  return pulse + (1 - pulse) * clamp01(steadyWeight);
}

export function isInfiniteHighlightPulseActive(highlight: PersistentHighlightData): boolean {
  return shouldPulseHighlightContinuously(highlight) && highlight.pulseCount === null;
}

/**
 * How long a `pulseCount` highlight runs before it clears itself — `pulseCount`
 * whole cycles of ITS pulse length — or `null` when it never does.
 */
export function highlightFinitePulseDurationSeconds(
  highlight: PersistentHighlightData,
): number | null {
  if (!shouldPulseHighlightContinuously(highlight) || highlight.pulseCount === null) {
    return null;
  }
  return (highlight.pulseCount * (highlight.pulseMs ?? HIGHLIGHT_DEFAULT_PULSE_MS)) / 1000;
}

export function isFiniteHighlightPulseActive(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
): boolean {
  const duration = highlightFinitePulseDurationSeconds(highlight);
  return duration !== null && elapsedSeconds < duration;
}

/** Unity `HasFinitePulseAnimationExpired` — the auto-clear trigger. */
export function hasFiniteHighlightPulseExpired(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
): boolean {
  const duration = highlightFinitePulseDurationSeconds(highlight);
  return duration !== null && elapsedSeconds >= duration;
}

// -- Color parsing (Unity `ColorUtility`) ------------------------------------

/**
 * Unity's named-color table (`ColorUtility.TryParseHtmlString` accepts these
 * case-insensitively alongside `#RGB` / `#RGBA` / `#RRGGBB` / `#RRGGBBAA`).
 * Kept because Unity accepts `colorHex: "red"` and the normalizer must fold it
 * to `#FF0000` on both clients.
 */
const K_NAMED_COLORS: Readonly<Record<string, string>> = {
  red: "ff0000",
  cyan: "00ffff",
  blue: "0000ff",
  darkblue: "0000a0",
  lightblue: "add8e6",
  purple: "800080",
  yellow: "ffff00",
  lime: "00ff00",
  fuchsia: "ff00ff",
  white: "ffffff",
  silver: "c0c0c0",
  grey: "808080",
  gray: "808080",
  black: "000000",
  orange: "ffa500",
  brown: "a52a2a",
  maroon: "800000",
  green: "008000",
  olive: "808000",
  navy: "000080",
  teal: "008080",
  aqua: "00ffff",
  magenta: "ff00ff",
};

/** Unity `ColorUtility.TryParseHtmlString`; alpha is parsed and then dropped. */
function tryParseHtmlColor(value: string): Rgb | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const named = K_NAMED_COLORS[trimmed.toLowerCase()];
  if (named) return hexTripletToRgb(named);

  if (!trimmed.startsWith("#")) return null;
  const digits = trimmed.slice(1).toLowerCase();
  if (!/^[0-9a-f]+$/.test(digits)) return null;
  if (digits.length === 3 || digits.length === 4) {
    return hexTripletToRgb(
      digits
        .slice(0, 3)
        .split("")
        .map((c) => c + c)
        .join(""),
    );
  }
  if (digits.length === 6 || digits.length === 8) return hexTripletToRgb(digits.slice(0, 6));
  return null;
}

function hexTripletToRgb(digits: string): Rgb {
  return [
    parseInt(digits.slice(0, 2), 16) / 255,
    parseInt(digits.slice(2, 4), 16) / 255,
    parseInt(digits.slice(4, 6), 16) / 255,
  ];
}

/** Unity `ColorUtility.ToHtmlStringRGB` — uppercase, `#`-prefixed, no alpha. */
export function toHtmlStringRgb(color: Rgb): string {
  const byte = (value: number) =>
    Math.min(255, Math.max(0, Math.round(value * 255)))
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${byte(color[0])}${byte(color[1])}${byte(color[2])}`;
}

// -- Math --------------------------------------------------------------------

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * A CSS `cubic-bezier(x1, y1, x2, y2)` timing function: solve the curve's x for
 * the input progress (Newton, then bisection where the slope is flat), return
 * its y. Endpoints are exact.
 */
function cubicBezierTiming(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number): number => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const error = sampleX(t) - x;
      if (Math.abs(error) < 1e-7) return sampleY(t);
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-7) break;
      t -= error / slope;
    }
    let low = 0;
    let high = 1;
    t = x;
    for (let i = 0; i < 40; i++) {
      const value = sampleX(t);
      if (Math.abs(value - x) < 1e-7) break;
      if (value < x) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return sampleY(t);
  };
}
